/* The review, on paper.
 *
 * The workspace is a two-pane reader: it shows one finding at a time, with its
 * evidence behind disclosures. Neither property survives printing. Paper has no
 * selection and nothing to expand, so a printed review has to be every finding,
 * every layer open, in reading order.
 *
 * That is what this is: a document that exists only in the print stylesheet
 * (`display: none` on screen, see Workspace.css), rendered alongside the panes
 * rather than instead of them.
 *
 * It renders the same layer bodies the cards do — `CheckBody` and
 * `ProvenanceBody`, lifted out of their disclosures for exactly this — so what
 * prints cannot drift from what the reviewer read on screen. What it does not
 * render is the controls: a decision is a thing you make in the application,
 * and on paper it is a fact, so it appears as one line of text.
 *
 * It carries no element ids. The cards on screen own `finding.id` and
 * `${finding.id}-title`, and a second copy of those would break both the
 * document pane's "Back to the finding" and the page's accessibility tree.
 */

import { CheckBody, ProvenanceBody, hasCheck } from '../finding';
import { EvidenceBadge, Label } from '../primitives';
import { evidenceBadgeFor, summaryLine } from '../../data/report';
import { describeDisposition, commentFor, locationChip } from '../../data/review';
import { groupByPlanArea } from '../../routes/workspaceModel';
import type { Disposition, Flag, ReviewRecord } from '../../types';
import './PrintReview.css';

export interface PrintReviewProps {
  docName: string;
  findings: Flag[];
  dispositions: Record<string, Disposition>;
  review: ReviewRecord | null;
  /** COPY.md's one line about the worked example, where there is one. */
  provenance: string | null;
}

export default function PrintReview({
  docName,
  findings,
  dispositions,
  review,
  provenance,
}: PrintReviewProps) {
  const groups = groupByPlanArea(findings);
  let number = 0;

  return (
    /* No aria-hidden: the block is display:none on screen, which already
       removes it from the accessibility tree, and aria-hidden over focusable
       descendants would be a defect rather than a precaution. */
    <div className="pr">
      <header className="pr__head">
        <Label mono>Pre-signature review</Label>
        <h1 className="pr__title">{docName}</h1>
        <p className="pr__summary">{summaryLine(findings)}</p>
        {provenance ? <p className="pr__provenance">{provenance}</p> : null}
        <p className="pr__provenance">
          Grouped by the part of the plan each finding is about. This review
          carries no severity, no score and no ranking, and it does not propose
          replacement contract language.
        </p>
      </header>

      {groups.map((group) => (
        <section className="pr__group" key={group.area}>
          <h2 className="pr__group-head">{group.label}</h2>
          {group.findings.map((finding) => {
            number += 1;
            return (
              <PrintFinding
                key={finding.id}
                finding={finding}
                number={number}
                disposition={dispositions[finding.id] ?? null}
                comment={commentFor(
                  { editedComments: review?.editedComments ?? {} },
                  finding.id,
                  finding.commentDraft,
                )}
              />
            );
          })}
        </section>
      ))}

      <footer className="pr__foot">
        <p>
          Ceiling quotes the clause and cites the federal audit that records the
          same cause. It does not rate severity, score contracts, predict
          outcomes, or propose replacement contract language.
        </p>
        <p>
          Only fifteen checks run. A finding of none is not a clean bill of
          health. Verify each finding against the source document before acting
          on it.
        </p>
      </footer>
    </div>
  );
}

function PrintFinding({
  finding,
  number,
  disposition,
  comment,
}: {
  finding: Flag;
  number: number;
  disposition: Disposition | null;
  comment: string;
}) {
  const chip = locationChip(finding.sectionRef, finding.page);
  const badge = evidenceBadgeFor(finding);

  return (
    <article className="pr__finding">
      <div className="pr__meta">
        <span className="pr__number">Finding {number}</span>
        {chip ? <span className="pr__chip">{chip}</span> : null}
        <EvidenceBadge id={badge.id} />
      </div>

      <h3 className="pr__claim">{finding.claim}</h3>
      {finding.contextLine ? (
        <p className="pr__context">{finding.contextLine}</p>
      ) : null}

      <p className="pr__decision">
        {disposition ? describeDisposition(disposition) : 'Not yet decided'}
        {disposition?.note ? ` — ${disposition.note}` : ''}
      </p>

      {/* The two layers, open, in the order the screen puts them in. */}
      {hasCheck(finding) ? (
        <div className="pr__layer">
          <Label>The check</Label>
          <CheckBody finding={finding} />
        </div>
      ) : null}

      <div className="pr__layer">
        <Label>Where this comes from</Label>
        <ProvenanceBody finding={finding} />
      </div>

      {disposition?.decision === 'include' ? (
        <div className="pr__layer">
          <Label>On the comment sheet</Label>
          <p className="pr__comment">{comment}</p>
        </div>
      ) : null}
    </article>
  );
}
